import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Toast from "../components/Toast";
import { useAuth } from "./AuthContext";

export type NotificationType = "success" | "error" | "info" | "warning"; // extended with warning (Toast supports it)

export interface NotificationState {
  id: string;
  message: string;
  type: NotificationType;
  group?: string; // logical group for replacement (e.g. job:<id>)
  createdAt: number;
  autoCloseMs?: number;
  persist?: boolean; // if true, won't auto dismiss
  /** Toast-only affordance. Ignored by the notification center list. */
  action?: { label: string; onClick: () => void };
}

// Legacy convenience signature support
export type ShowNotificationFn = {
  // Modern object form
  (opts: Omit<NotificationState, "createdAt" | "id"> & { id?: string }): string;
  // Legacy positional form: (message, type?, extras?)
  (
    message: string,
    type?: NotificationType,
    extras?: Partial<
      Pick<NotificationState, "group" | "autoCloseMs" | "persist">
    > & {
      id?: string;
    }
  ): string;
};

interface NotificationContextValue {
  notifications: NotificationState[];
  showNotification: ShowNotificationFn; // returns id
  closeNotification: (id: string) => void;
  closeGroup: (group: string) => void;
  replaceGroup: (
    group: string,
    opts: Omit<NotificationState, "createdAt" | "id" | "group"> & {
      id?: string;
    }
  ) => string;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined
);

export const LOGIN_GROUP_PREFIX = "login:";

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationState[]>([]);
  const timersRef = useRef<Record<string, number>>({});
  const { user } = useAuth();
  const previousUserIdRef = useRef<string | null>(null);

  const scheduleAutoClose = useCallback((id: string, ms?: number) => {
    if (!ms) return;
    if (timersRef.current[id]) clearTimeout(timersRef.current[id]);
    timersRef.current[id] = window.setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      delete timersRef.current[id];
    }, ms);
  }, []);

  const closeNotification = useCallback((id: string) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const closeGroup = useCallback((group: string) => {
    setNotifications((prev) => prev.filter((n) => n.group !== group));
  }, []);

  const showNotification = useCallback<ShowNotificationFn>(
    (
      arg1:
        | (Omit<NotificationState, "createdAt" | "id"> & { id?: string })
        | string,
      maybeType?: NotificationType,
      extras?: Partial<
        Pick<NotificationState, "group" | "autoCloseMs" | "persist">
      > & {
        id?: string;
      }
    ) => {
      // Normalize arguments to object form
      const opts: Omit<NotificationState, "createdAt" | "id"> & {
        id?: string;
      } =
        typeof arg1 === "string"
          ? {
              message: arg1,
              type: maybeType || "info",
              group: extras?.group,
              autoCloseMs: extras?.autoCloseMs,
              persist: extras?.persist,
              id: extras?.id,
            }
          : arg1;

      const isLoginGroup = !!opts.group?.startsWith(LOGIN_GROUP_PREFIX);
      const hasActiveUser = !!user;
      if (!hasActiveUser && !isLoginGroup) {
        // Prevent showing non-login notifications when there's no active user
        return crypto.randomUUID();
      }

      if (!opts.message || opts.message.trim() === "") {
        // Silently ignore empty message requests to avoid blank toasts
        return crypto.randomUUID();
      }
      const id = opts.id || crypto.randomUUID();
      setNotifications((prev) => {
        const existingIdx = prev.findIndex((n) => n.id === id);
        const next = [...prev];
        const record: NotificationState = {
          id,
          message: opts.message,
          // Ensure unknown type falls back to info
          type: (opts.type as NotificationType) || "info",
          group: opts.group,
          createdAt: Date.now(),
          autoCloseMs: opts.autoCloseMs ?? (opts.type === "info" ? 4000 : 6000),
          persist: opts.persist,
          action: opts.action,
        };
        if (existingIdx >= 0) next[existingIdx] = record;
        else next.push(record);
        return next;
      });
      scheduleAutoClose(
        id,
        opts.persist
          ? undefined
          : opts.autoCloseMs ?? (opts.type === "info" ? 4000 : 6000)
      );
      return id;
    },
    [scheduleAutoClose, user]
  );

  const replaceGroup = useCallback(
    (
      group: string,
      opts: Omit<NotificationState, "createdAt" | "id" | "group"> & {
        id?: string;
      }
    ) => {
      // Remove existing in group then add new
      setNotifications((prev) => prev.filter((n) => n.group !== group));
      return showNotification({ ...opts, group });
    },
    [showNotification]
  );

  const clearAll = useCallback(() => {
    Object.values(timersRef.current).forEach((t) => clearTimeout(t));
    timersRef.current = {};
    setNotifications([]);
  }, []);

  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (!currentUserId && previousUserIdRef.current) {
      // User just signed out; keep only login-related notifications (if any)
      setNotifications((prev) =>
        prev.filter((n) => n.group?.startsWith(LOGIN_GROUP_PREFIX))
      );
    }
    previousUserIdRef.current = currentUserId;
  }, [user?.id]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        showNotification,
        closeNotification,
        closeGroup,
        replaceGroup,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext);
  if (!ctx)
    throw new Error(
      "useNotificationContext must be used within NotificationProvider"
    );
  return ctx;
};

/**
 * Same context without the provider requirement: returns undefined outside it.
 * For components that also render in isolation (tests, storybook) and treat the
 * toast as an enhancement over their own live region, not a dependency.
 */
export const useOptionalNotificationContext = ():
  | NotificationContextValue
  | undefined => useContext(NotificationContext);

export const NotificationToaster: React.FC = () => {
  const { notifications, closeNotification } = useNotificationContext();
  return (
    <div className="fixed top-5 end-5 z-[50000] flex flex-col gap-3 w-full max-w-sm">
      {notifications
        .sort((a, b) => a.createdAt - b.createdAt)
        .map((n) => (
          <Toast
            key={n.id}
            notification={{ message: n.message, type: n.type, action: n.action }}
            onClose={() => closeNotification(n.id)}
          />
        ))}
    </div>
  );
};
